import React from "react";
import {Link, Link as RouterLink} from "react-router-dom";
import {
    IconButton,
    makeStyles,
    Typography
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableContainer
    from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import {Delete, Edit} from "@material-ui/icons";
import styled from "styled-components";
import {
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useTable,
} from "react-table";
import apiHelper from "../../../../util/ApiHelper";
import {CustomCheckbox, modal} from "../../../../util";
import {useLoading} from "../../../../util/LoadingProvider";
import {
    PaginationHandler
} from "../../../../util/useManualPagination";
import {toast} from "react-toastify";
import dayjs from "dayjs";
import PaginationBarForHandler from "../../../common/PaginationBarForHandler";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DragHandleIcon from "@material-ui/icons/DragHandle";

const useStyles = makeStyles((theme) => ({
    content: {
        "& .ck-editor__editable_inline": {
            minHeight: "200px",
        },
    },
    text: {
        textDecoration: "none",
        color: "#306F9D",
    },
    pagination: {
        "& .MuiTextField-root": {
            width: "35px",
            minWidth: "auto",
        },
        "& .MuiSelect-root": {
            paddingTop: "3px",
            paddingBottom: "3px",
        },
        "& input": {
            padding: "4px 0",
            marginLeft: "10px",
            marginRight: "10px",
        },
        "& button": {
            background: "white",
            color: "#cccccc",
            border: "1px solid #CCCCCC",
            minWidth: "auto",
            width: "24px",
            height: "24px",
            margin: "4px 0",
            "&.left": {
                borderRadius: "4px 0 0 4px",
                marginLeft: "10px",
            },
            "&.right": {
                borderRadius: " 0 4px 4px 0",
            },
        },
    },
}));

const statuses = Object.freeze({
    ACTIVE: "active",
    INACTIVE: "inactive",
});

const IsDraggingContext = React.createContext(false);

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

class DraggableTableCell extends React.Component {
  getSnapshotBeforeUpdate(prevProps) {
    if (!this.ref) {
      return null;
    }

    const isDragStarting =
      this.props.isDragOccurring && !prevProps.isDragOccurring;

    if (!isDragStarting) {
      return null;
    }

    const { width, height } = this.ref.getBoundingClientRect();

    const snapshot = {
      width,
      height,
    };

    return snapshot;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const ref = this.ref;
    if (!ref) {
      return;
    }

    if (snapshot) {
      if (ref.style.width === snapshot.width) {
        return;
      }
      ref.style.width = `${snapshot.width}px`;
      ref.style.height = `${snapshot.height}px`;
      return;
    }

    if (this.props.isDragOccurring) {
      return;
    }

    // inline styles not applied
    if (ref.style.width == null) {
      return;
    }

    // no snapshot and drag is finished - clear the inline styles
    ref.style.removeProperty("height");
    ref.style.removeProperty("width");
  }

  setRef = (ref) => {
    this.ref = ref;
  };

  render() {
    return (
      <TableCell
        style={{ paddingTop: 12, paddingBottom: 12 }}
        ref={this.setRef}
        align={this.props.align}
      >
        {this.props.children}
      </TableCell>
    );
  }
}

const DraggableTableRow = ({
  snapshot,
  data,
  row,
  provided,
  className,
  text,
  orderingDisabled,
  match,
}) => {
  return (
    <IsDraggingContext.Consumer>
      {(isDragging) => (
        <TableRow
          className={className}
          ref={provided.innerRef}
          isDragging={snapshot.isDragging}
          {...provided.draggableProps}
        >
          {row.cells.map((cell, index) => {
            return (
                <DraggableTableCell isDragOccurring={isDragging} scope="row">
                    {row.cells[index].render("Cell")}
                </DraggableTableCell>
            );
          })}
          <TableCell
            style={{ paddingBottom: 12, color: "#CCCCCC" }}
            {...(!orderingDisabled && { ...provided.dragHandleProps })}
          >
            <DragHandleIcon />
          </TableCell>
        </TableRow>
      )}
    </IsDraggingContext.Consumer>
  );
};

const IndeterminateCheckbox = React.forwardRef(
    ({indeterminate, ...rest}, ref) => {
        const defaultRef = React.useRef();
        const resolvedRef = ref || defaultRef;

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate;
        }, [resolvedRef, indeterminate]);

        return <CustomCheckbox type="checkbox"
                               ref={resolvedRef} {...rest} />;
    }
);

const StatusFilter = styled.ul`
  list-style: none;
  display: inline-flex;
  padding: 0;

  li {
    button {
      border: 0px;
      background: transparent;
      color: #306f9d;
      font-size: 13px;
      line-height: 15px;
      padding: 0 10px;
      cursor: pointer;

      &.active {
        font-weight: bold;
        color: #333333;
      }
    }

    &:not(:last-of-type) {
      button {
        border-right: 1px solid black;
      }
    }

    &:first-of-type {
      button {
        padding-left: 0;
      }
    }
  }
`;

const formatDatetime = (dt) => {
    return dayjs(dt).format("DD MMM YYYY, hh:mma");
};

const SponsorsListingPage = (props) => {
    const {match, disabled} = props;
    const classes = useStyles();
    const [status, setStatus] = React.useState(null);
    const [actionType, setActionType] = React.useState("");
    const [keyword, setKeyword] = React.useState("");
    const [data, setData] = React.useState([]);
    const [totalPages, setTotalPages] = React.useState(1);
    const [categoryId, setCategoryId] = React.useState("");
    const [categoryList, setCategoryList] = React.useState([]);
    const [orderingDisabled, setOrderingDisabled] = React.useState(true);
    const [isDragging, setIsDragging] = React.useState(false);

    const {setLoading} = useLoading();

    // for pagination
    const [paginationHandler] = React.useState(new PaginationHandler());
    const [elementCounts, setElementCounts] = React.useState({
        all: 0,
        active: 0,
        inactive: 0,
    });

    const CustomLink = React.useMemo(
        () =>
            React.forwardRef((linkProps, ref) => <Link
                ref={ref} {...linkProps} />),
        []
    );

    const handleSelectChange = (e) => {
        setActionType(e.target.value);
    };

    const handleCategorySelectChange = (e) => {
        setCategoryId(e.target.value);
    };

    const deleteSponsor = async (id) => {
        const decision = await modal.fire({
            title: "Delete Sponsor",
            text: "Are you sure to delete this sponsor?",
            showCancelButton: true,
            confirmButtonText: `Delete`,
        });
        if (decision.isConfirmed) {
            try {
                const result = await apiHelper.hkiff.deleteSponsorById(id);
                if (result.status === 200) {
                    toast.dark("sponsor deleted.");
                    await getSponsor();
                }
            } catch (err) {
                console.log(err);
            }
        }
    };

    const columns = React.useMemo(
        () => [
            {
                Header: "Title",
                accessor: "localizations.en.title",
                Cell: ({row}) => {
                    return (
                        <Link
                            to={`${match.url}/${row.original.id}`}
                            className={classes.text}
                        >
                            {row.original.localizations &&
                                row.original.localizations.en &&
                                row.original.localizations.en.title}
                        </Link>
                    );
                },
            },
            {
                Header: "Status",
                accessor: "status",
                disableGlobalFilter: true,
                Cell: ({row}) => {
                    return (
                        <span className={classes.text}>
              {row.original.status === 0
                  ? statuses.INACTIVE
                  : row.original.status === 1
                      ? statuses.ACTIVE
                      : null}
            </span>
                    );
                },
            },
            {
                Header: "Last Update",
                accessor: "updateTime",
                disableGlobalFilter: true,
                Cell: ({row}) => {
                    return formatDatetime(row.original.updateTime);
                },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const {
        getTableProps,
        headerGroups,
        rows,
        prepareRow,
        setPageSize,
        gotoPage,
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
        selectedFlatRows,
        state: {pageIndex, pageSize},
        getTableBodyProps,
    } = useTable(
        {
            columns,
            data,
            initialState: {pageIndex: 0, pageSize: 10},
            manualPagination: true,
            pageCount: totalPages,
            autoResetPage: false,
        },
        useGlobalFilter,
        usePagination,
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                // Let's make a column for selection
                {
                    id: "selection",
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: ({getToggleAllRowsSelectedProps}) => (
                        <div>
                            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                        </div>
                    ),
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({row}) => (
                        <div>
                            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                        </div>
                    ),
                },
                ...columns,
                {
                    id: "action",
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: () => <div>Action</div>,
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({row}) => (
                        <div
                            style={{display: "inline-flex"}}>
                            <IconButton
                                size="small"
                                component={CustomLink}
                                to={`${match.url}/${row.original.id}`}
                            >
                                <Edit fontSize="small"/>
                            </IconButton>
                            {disabled || (
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        deleteSponsor(row.original.id);
                                    }}
                                >
                                    <Delete
                                        fontSize="inherit"/>
                                </IconButton>
                            )}
                        </div>
                    ),
                },
              {
                id: "dragHandle",
                Header: () => <></>,
              },
            ]);
        }
    );

    const getSponsor = React.useCallback(async () => {
        try {
            setLoading(true);
            const result = await apiHelper.hkiff.getSponsor(0, 0, 9999999);
            paginationHandler.setPageIndex(0).setRows(result.data.content);
            const content = paginationHandler.filterRowsByAll().getRows();
            const totalPages = paginationHandler.getTotalPages();
            const all = paginationHandler.getStatuslessRows().length;
            const active = paginationHandler
                .getStatuslessRows()
                .filter((x) => x && x.status).length;
            const inactive = all - active;
            setElementCounts({all, active, inactive});
            setData(content.filter((x) => x));
            setTotalPages(totalPages);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [paginationHandler, setLoading]);

    React.useEffect(() => {
        if (paginationHandler) {
            getSponsor(); // only get sponsors if paginationHandler ready
        }
    }, [getSponsor, paginationHandler]);

    const getSponsorCategoryList = React.useCallback(async () => {
        try {
            setLoading(true);
            const result = await apiHelper.hkiff.getSponsorsCategory(
                null,
                0,
                0,
                9999999,
                ''
            );
            setCategoryList(result.data.content);
            // console.log("category list:", result.data.content);
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    }, [setLoading])

    React.useEffect(() => {
        getSponsorCategoryList();
    }, []);

    const statusFilterHandler = (targetStatus) => () => {
        setLoading(true);
        setStatus(targetStatus);
        let target;
        if (targetStatus === statuses.ACTIVE) {
            target = 1;
        } else if (targetStatus === statuses.INACTIVE) {
            target = 0;
        } else {
            target = null;
        }
        paginationHandler
            .resetRows()
            .setQueryStatus("status", target)
            .filterRowsByAll();
        setData(paginationHandler.getRows());
        gotoPage(0);
        setLoading(false);
    };

    const triggerSearch = React.useCallback(() => {
        setLoading(true);
        setOrderingDisabled(keyword ? true : false);
        paginationHandler
            .resetRows()
            .setQueryString("localizations.en.title", keyword)
            .filterRowsByAll();
        setData(paginationHandler.getRows());
        const all = paginationHandler.getStatuslessRows().length;
        const active = paginationHandler
            .getStatuslessRows()
            .filter((x) => x && x.status).length;
        const inactive = all - active;
        setElementCounts({all, active, inactive});
        gotoPage(0);
        setLoading(false);
    }, [gotoPage, keyword, paginationHandler, setLoading]);

    const triggerCategoryFilter = React.useCallback(() => {
        setLoading(true);
        setOrderingDisabled(categoryId ? false : true);
        console.log("queryCategoryId: " + categoryId);
        if (categoryId === "" || categoryId === null) {
            paginationHandler.resetFilters().resetRows();
        } else {
            paginationHandler
            .resetRows()
            .setQueryArray("categories", "id", categoryId)
            .filterRowsByAll();
        }

        setData(paginationHandler.getRows());
        const all = paginationHandler.getStatuslessRows().length;
        const active = paginationHandler
            .getStatuslessRows()
            .filter((x) => x && x.status).length;
        const inactive = all - active;
        setElementCounts({
            all, active, inactive
        });
        gotoPage(0);
        setLoading(false);
    }, [categoryId]);

    const handleBulkAction = React.useCallback(async () => {
        switch (actionType) {
            case "DELETE":
                const ids = selectedFlatRows.map((o) => o.original.id);
                if (ids.length > 0) {
                    const decision = await modal.fire({
                        title: "Delete Sponsor",
                        text: `Are you sure to delete ${ids.length} sponsor(s)?`,
                        showCancelButton: true,
                        confirmButtonText: `Delete`,
                    });
                    if (decision.isConfirmed) {
                        try {
                            const promises = ids.map((id) =>
                                apiHelper.hkiff.deleteSponsorById(id)
                            );
                            await Promise.all(promises);
                            toast.dark(`${ids.length} sponsor(s) deleted.`);
                            await getSponsor();
                        } catch (err) {
                            console.log(err);
                        }
                    }
                }
                break;
            default:
                break;
        }
    }, [actionType, selectedFlatRows, getSponsor]);

    const saveOrdering = async () => {
      try {
        const seqData = {};
        rows.forEach((e, i) => {
          seqData[e.original.id] = pageIndex * pageSize + i;
        });

        console.log('seqData:', seqData);

        const result = await apiHelper.hkiff.updateSponsorSeq(categoryId, seqData);
        if (result.data.code === 0) {
          toast.dark("Your changes are saved.");
          getSponsor();
        }
      } catch (err) {
        console.log(err);
      }
    };

    const onBeforeDragStart = () => {
      setIsDragging(true);
    };

    //callback passed to dragdrop context
    const onDragEnd = (result) => {
      // dropped outside the list
      if (!result.destination) {
        return;
      }

      const items = reorder(
        data,
        result.source.index,
        result.destination.index
      );
      console.log("reordered:", items);
      console.log("pagesize", pageSize);
      console.log("pageindex", pageIndex);

      setData(items);
    };

    return (
        <div className={classes.content}>
            <Typography variant="h4" gutterBottom>
                {"Sponsors > Sponsors List"}
            </Typography>
            <form>
                <StatusFilter>
                    <li>
                        <button
                            className={status === null ? "active" : ""}
                            type="button"
                            onClick={statusFilterHandler(null)}
                        >
                            <Typography variant="p">All
                                ({elementCounts.all})</Typography>
                        </button>
                    </li>
                    <li>
                        <button
                            className={status === statuses.ACTIVE ? "active" : ""}
                            type="button"
                            onClick={statusFilterHandler(statuses.ACTIVE)}
                        >
                            <Typography variant="p">
                                Active
                                ({elementCounts.active})
                            </Typography>
                        </button>
                    </li>
                    <li>
                        <button
                            className={status === statuses.INACTIVE ? "active" : ""}
                            type="button"
                            onClick={statusFilterHandler(statuses.INACTIVE)}
                        >
                            <Typography variant="p">
                                Inactive
                                ({elementCounts.inactive})
                            </Typography>
                        </button>
                    </li>
                </StatusFilter>
                <Grid
                    container
                    justify="space-between"
                    style={{paddingBottom: "10px"}}
                >
                    <Grid container item xs={6} md={6}>
                        <Box
                            width="100%"
                            component="span"
                            display="flex"
                            alignItems="center"
                        >
                            <Box flex>
                                <FormControl fullWidth
                                             margin="dense"
                                             variant="outlined">
                                    <InputLabel
                                        id="demo-simple-select-outlined-label">
                                        Bulk Actions
                                    </InputLabel>
                                    <Select
                                        labelId="demo-simple-select-outlined-label"
                                        id="demo-simple-select-outlined"
                                        value={actionType}
                                        onChange={handleSelectChange}
                                        label="Bulk Actions"
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        <MenuItem
                                            value={"DELETE"}>Delete</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box flex>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleBulkAction}
                                    disabled={disabled}
                                >
                                    Apply
                                </Button>
                            </Box>
                            <Box flex>
                                <Button
                                variant="contained"
                                color="primary"
                                onClick={saveOrdering}
                                disabled={orderingDisabled || disabled || status !== null}
                                >
                                Save Changes
                                </Button>
                            </Box>
                            <Box display="flex">
                                <FormControl fullWidth
                                    margin="dense"
                                    variant="outlined">
                                    <InputLabel
                                        id="simple-select-outlined-label">
                                        Category
                                    </InputLabel>
                                    <Select
                                        labelId="simple-select-outlined-label"
                                        id="simple-select-outlined"
                                        value={categoryId}
                                        onChange={handleCategorySelectChange}
                                        label="Category"
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {categoryList.map((option) => (
                                            <MenuItem value={option.id}>
                                                {option.localizations.en.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box display="flex">
                                <Button
                                    type="button"
                                    variant="outlined"
                                    color="primary"
                                    onClick={triggerCategoryFilter}
                                >
                                    Filter
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid container item xs={6} md={6}>
                        <Box
                            width="100%"
                            component="span"
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                        >
                            <Box display="flex">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="button"
                                    component={RouterLink}
                                    to={`${match.url}/new`}
                                    disabled={disabled}
                                >
                                    Add New Sponsor
                                </Button>
                            </Box>
                            <Box display="flex">
                                <TextField
                                    id="outlined-error"
                                    label="Search"
                                    variant="outlined"
                                    margin="dense"
                                    value={keyword}
                                    onChange={(e) => setKeyword(e.target.value)}
                                />
                            </Box>
                            <Box display="flex">
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    type="button"
                                    margin="dense"
                                    onClick={triggerSearch}
                                >
                                    Search
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
                <Grid container xs={12}>
                    <TableContainer
                        component={Paper}
                        style={{
                            boxShadow: "none",
                            border: "1px solid #cccccc",
                        }}
                    >
                        <fieldset disabled={disabled}
                                  className="fieldset">
                          <IsDraggingContext.Provider value={isDragging}>
                            <Table
                                aria-label="simple table"
                                component="ul"
                                style={{margin: "0"}}
                                {...getTableProps()}
                            >
                                <colgroup>
                                    <col
                                        style={{width: "5%"}}/>
                                    <col
                                        style={{width: "60%"}}/>
                                    <col
                                        style={{width: "10%"}}/>
                                    <col
                                        style={{width: "20%"}}/>
                                    <col
                                        style={{width: "5%"}}/>
                                </colgroup>
                                <TableHead component="div">
                                    {headerGroups.map((headerGroup) => (
                                        <TableRow {...headerGroup.getHeaderGroupProps()}>
                                            {headerGroup.headers.map((column) => (
                                                <TableCell
                                                    {...column.getHeaderProps()}
                                                    className={`${column.className ?? ""}`}
                                                    style={{
                                                        paddingTop: 6,
                                                        paddingBottom: 6,
                                                        lineHeight: "1.1em",
                                                        height: "48px",
                                                    }}
                                                >
                                                    {column.render("Header")}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableHead>
                                <DragDropContext
                                    onBeforeDragStart={onBeforeDragStart}
                                    onDragEnd={onDragEnd}
                                    >
                                    <Droppable droppableId="droppable" type="QUESTIONS">
                                        {(provided, snapshot) => (
                                    <TableBody
                                        component="div"
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        {...getTableBodyProps()}
                                    >
                                    {rows.map((row, index) => {
                                        prepareRow(row);
                                        return (
                                            <Draggable
                                                key={row.id}
                                                draggableId={row.id}
                                                index={index}
                                                isDragDisabled={disabled || status !== null}
                                                >
                                                {(provided, snapshot) => (
                                                    <DraggableTableRow
                                                    key={row.id}
                                                    data={row.original}
                                                    row={row}
                                                    provided={provided}
                                                    snapshot={snapshot}
                                                    text={classes.text}
                                                    orderingDisabled={orderingDisabled}
                                                    match={match}
                                                    />
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                     {provided.placeholder}
                                </TableBody>
                                )}
                                </Droppable>
                                </DragDropContext>
                            </Table>
                            </IsDraggingContext.Provider>
                        </fieldset>
                    </TableContainer>
                </Grid>
                <Grid
                    container
                    justify="flex-end"
                    style={{
                        paddingRight: "25px",
                        marginTop: "15px"
                    }}
                >
                    <div>
                        <div></div>
                        <PaginationBarForHandler
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            setData={setData}
                            setTotalPages={setTotalPages}
                            paginationHandler={paginationHandler}
                            gotoPage={gotoPage}
                            pageIndex={pageIndex}
                            canPreviousPage={canPreviousPage}
                            previousPage={previousPage}
                            nextPage={nextPage}
                            canNextPage={canNextPage}
                            elementCounts={elementCounts}
                            status={status}
                        />
                    </div>
                </Grid>
            </form>
        </div>
    );
};

export default SponsorsListingPage;
